/**
 * Widget E: Retirement Income Sources
 * 
 * Displays a vertical bar chart showing the average annual income
 * from different retirement sources: TSP, 401(k), 403(b), Social Security, Annuities, Other.
 */

import React, { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateRetirementIncomeSources, RetirementIncomeSourcesData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, ACCENT_COLOR } from "./widgetColors";
import { ChartTooltip, BAR_CHART_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface RetirementIncomeSourcesWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

// Color mapping function that handles various source name formats
const getSourceColor = (sourceName: string): string => {
  const normalized = sourceName.toLowerCase().trim();
  
  // TSP variations
  if (normalized === "tsp" || normalized.includes("thrift")) {
    return UNIFIED_CHART_COLORS[1]; // Sky blue
  }
  
  // 401k variations
  if (normalized === "401k" || normalized === "401(k)" || normalized.includes("401")) {
    return UNIFIED_CHART_COLORS[2]; // Lavender
  }
  
  // 403b variations
  if (normalized === "403b" || normalized === "403(b)" || normalized.includes("403")) {
    return UNIFIED_CHART_COLORS[7]; // Rose pink
  }
  
  // Social Security variations
  if (normalized === "socialsecurity" || normalized === "social security" || 
      normalized.includes("social") || normalized.includes("ss")) {
    return UNIFIED_CHART_COLORS[6]; // Mint green
  }
  
  // Annuity variations
  if (normalized === "annuity" || normalized.includes("annuity")) {
    return UNIFIED_CHART_COLORS[3]; // Peach
  }
  
  // Other/fallback
  return UNIFIED_CHART_COLORS[9]; // Blue-gray
};

const SOURCE_COLORS = {
  tsp: UNIFIED_CHART_COLORS[1],           // Sky blue (index 1)
  "401k": UNIFIED_CHART_COLORS[2],         // Lavender (index 2)
  "403b": UNIFIED_CHART_COLORS[7],         // Rose pink (index 7)
  socialSecurity: UNIFIED_CHART_COLORS[6], // Mint green (index 6)
  annuity: UNIFIED_CHART_COLORS[3],        // Peach (index 3)
  other: UNIFIED_CHART_COLORS[9],          // Blue-gray (index 9)
};

const SOURCE_LABELS = {
  tsp: "TSP",
  "401k": "401(k)",
  "403b": "403(b)",
  socialSecurity: "Social Security",
  annuity: "Annuity",
  other: "Other",
};

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

export function RetirementIncomeSourcesWidget({
  clients,
  isLoading = false,
  userId,
}: RetirementIncomeSourcesWidgetProps) {
  const api = XanoAPI.getInstance();

  // Fetch data from API endpoint
  const { data: apiData, isLoading: apiLoading, error: apiError } = useQuery<{
    sources: Array<{ source: string; average: number }>;
    totalAverage: number;
  }>({
    queryKey: ["retirement-income-sources", userId],
    queryFn: async (): Promise<{
      sources: Array<{ source: string; average: number }>;
      totalAverage: number;
    }> => {
      const result = await api.getRetirementIncomeSources(userId);
      console.log("🔍 RetirementIncomeSourcesWidget: API response:", result);
      console.log("🔍 RetirementIncomeSourcesWidget: API response type:", typeof result);
      console.log("🔍 RetirementIncomeSourcesWidget: Has sources?", !!result?.sources);
      console.log("🔍 RetirementIncomeSourcesWidget: Sources count:", result?.sources?.length);
      console.log("🔍 RetirementIncomeSourcesWidget: Total average:", result?.totalAverage);
      if (result.sources && result.sources.length > 0) {
        console.log("🔍 RetirementIncomeSourcesWidget: Sources details:", JSON.stringify(result.sources, null, 2));
      }
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Log any errors
  useEffect(() => {
    if (apiError) {
      console.error("❌ RetirementIncomeSourcesWidget: Query error:", apiError);
    }
  }, [apiError]);

  const data = useMemo(() => {
    // Only use API data - no sample data fallback
    let result: RetirementIncomeSourcesData;
    
    console.log("🔍 RetirementIncomeSourcesWidget: Processing data - apiData:", apiData);
    console.log("🔍 RetirementIncomeSourcesWidget: apiData is null?", apiData === null);
    console.log("🔍 RetirementIncomeSourcesWidget: apiData is undefined?", apiData === undefined);
    
    if (apiData && apiData.sources && Array.isArray(apiData.sources)) {
      console.log("✅ RetirementIncomeSourcesWidget: Using API data");
      console.log("🔍 RetirementIncomeSourcesWidget: Sources:", JSON.stringify(apiData.sources, null, 2));
      console.log("🔍 RetirementIncomeSourcesWidget: Sources count:", apiData.sources.length);
      console.log("🔍 RetirementIncomeSourcesWidget: totalAverage:", apiData.totalAverage);
      
      // Use API data even if totalAverage is 0 (we'll calculate it if needed)
      result = {
        sources: apiData.sources,
        totalAverage: apiData.totalAverage || 0,
      };
    } else {
      // No data available - return empty structure
      console.log("❌ RetirementIncomeSourcesWidget: No API data, returning empty structure");
      result = {
        sources: [],
        totalAverage: 0,
      };
    }
    
    // Transform to chart data format - filter out zero sources and sort by average descending
    const chartData = result.sources
      .filter((s) => s.average > 0)
      .map((source) => {
        const sourceKey = source.source.toLowerCase().trim();
        const normalizedKey = sourceKey === "401k" || sourceKey === "401(k)" ? "401k" :
                             sourceKey === "403b" || sourceKey === "403(b)" ? "403b" :
                             sourceKey === "socialsecurity" || sourceKey === "social security" ? "socialSecurity" :
                             sourceKey;
        
        return {
          source: SOURCE_LABELS[normalizedKey as keyof typeof SOURCE_LABELS] || 
                  SOURCE_LABELS[source.source as keyof typeof SOURCE_LABELS] || 
                  source.source,
          average: source.average,
          fill: SOURCE_COLORS[normalizedKey as keyof typeof SOURCE_COLORS] || 
                SOURCE_COLORS[source.source as keyof typeof SOURCE_COLORS] || 
                getSourceColor(source.source),
          key: source.source,
        };
      })
      .sort((a, b) => b.average - a.average);
    
    // Log color assignments for debugging
    console.log("🔍 RetirementIncomeSourcesWidget: Color assignments:", 
      chartData.map(d => ({ source: d.source, color: d.fill })));
    
    console.log("🔍 RetirementIncomeSourcesWidget: Chart data after filtering:", chartData);
    console.log("🔍 RetirementIncomeSourcesWidget: Chart data length:", chartData.length);
    console.log("🔍 RetirementIncomeSourcesWidget: Total average:", result.totalAverage);

    return { chartData, result };
  }, [apiData]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.chartData.length === 0 || data.result.totalAverage === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No retirement income data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.chartData}
              margin={BAR_CHART_MARGINS}
            >
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="source" 
                angle={-45}
                textAnchor="end"
                height={70}
                interval={0}
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Average Annual Income", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                tick={AXIS_STYLE.tick}
                tickFormatter={formatCurrency}
                tickLine={false}
                width={80}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = data.chartData.find(d => d.average === value);
                    const percentage = data.result.totalAverage > 0
                      ? ((value / data.result.totalAverage) * 100).toFixed(1)
                      : "0.0";
                    return [`${formatCurrency(value)} (${percentage}%)`, dataPoint?.source || name];
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="average" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item) => {
            const percentage = data.result.totalAverage > 0
              ? ((item.average / data.result.totalAverage) * 100).toFixed(1)
              : "0.0";
            return (
              <div key={item.key} className="space-y-1">
                <div className="text-sm font-bold" style={{ color: item.fill }}>
                  {formatCurrency(item.average)}
                </div>
                <div className="text-xs font-medium text-foreground">{item.source}</div>
                <div className="text-xs text-muted-foreground">{percentage}%</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

